import { registerBlockType } from '@wordpress/blocks';
import { addFilter } from '@wordpress/hooks';
import { createHigherOrderComponent } from '@wordpress/compose';

import { settings as entriesBlock } from './blocks/entries';
import { settings as entriesTableBlock } from './blocks/entries-table';
import { settings as entriesLoopBlock } from './blocks/entries-loop';
import { settings as paginationBlock } from './blocks/pagination';
import { settings as viewEntryBlock } from './blocks/view-entry';
import { settings as editEntryBlock } from './blocks/edit-entry';
import { settings as editFormBlock } from './blocks/edit-form';
import { settings as filtersBlock } from './blocks/filters';
import { settings as entriesIndexBlock } from './blocks/entries-index';
import MergeTagToolbar from './components/controls-block/MergeTagToolbar';
import ViewSelectorToolbar from './components/controls-block/ViewSelectorToolbar';
import ConditionalLogicPanel from './components/controls-inspector/ConditionalLogicPanel';
import { appendMergeTagCompleter } from './autocompleters/mergeTagCompleter';

/**
 * Register store as a side-effect.
 */
import './stores/gpeb';

registerBlockType('gp-entry-blocks/entries', entriesBlock);
registerBlockType('gp-entry-blocks/entries-table', entriesTableBlock);
registerBlockType('gp-entry-blocks/entries-loop', entriesLoopBlock);
registerBlockType('gp-entry-blocks/pagination', paginationBlock);
registerBlockType('gp-entry-blocks/view-entry', viewEntryBlock);
registerBlockType('gp-entry-blocks/edit-entry', editEntryBlock);
registerBlockType('gp-entry-blocks/edit-form', editFormBlock);
registerBlockType('gp-entry-blocks/filters', filtersBlock);
registerBlockType('gp-entry-blocks/entries-index', entriesIndexBlock);

/**
 * Add filters
 */
addFilter(
	'editor.BlockEdit',
	'gp-entry-blocks/merge-tag-toolbar',
	MergeTagToolbar
);

addFilter(
	'editor.BlockEdit',
	'gp-entry-blocks/view-selector-toolbar',
	ViewSelectorToolbar
);

addFilter(
	'editor.Autocomplete.completers',
	'gp-entry-blocks/autocompleters/merge-tags',
	appendMergeTagCompleter
);

addFilter(
	'editor.BlockEdit',
	'gp-entry-blocks/conditional-logic',
	ConditionalLogicPanel
);

/**
 * Add a CSS class to blocks that have conditional logic enabled.
 */
const withConditionalLogicClassName = createHigherOrderComponent(
	(BlockListBlock) =>
		(
			props: BlockProps<any> & {
				attributes: BlockProps<any>['attributes'] & {
					gpebConditionalLogicEnabled: boolean;
					gpebConditionalLogic: GFConditionalLogic;
				};
			}
		) => {
			const { attributes } = props;

			const { gpebConditionalLogicEnabled, gpebConditionalLogic } =
				attributes;

			const hasConditions =
				gpebConditionalLogic?.actionType &&
				gpebConditionalLogic?.rules?.length;

			if (gpebConditionalLogicEnabled && hasConditions) {
				let gpebClClassName = `gpeb-has-conditional-logic`;

				if (gpebConditionalLogic.actionType === 'show') {
					gpebClClassName += ` gpeb-conditionally-show`;
				} else {
					gpebClClassName += ` gpeb-conditionally-hide`;
				}

				return (
					<BlockListBlock {...props} className={gpebClClassName} />
				);
			}

			return <BlockListBlock {...props} />;
		},
	'withClientIdClassName'
);

addFilter(
	'editor.BlockListBlock',
	'conditionalBlocks/editor/blockClasses',
	withConditionalLogicClassName
);

/*
 * Add conditional logic attributes to all blocks except those from this plugin.
 *
 * We also do this on the PHP side otherwise when requesting block previews using the REST API, it can throw an error.
 *
 * Lastly, gp-entry-blocks/edit-form has these attributes defined in its block.json
 */
addFilter(
	'blocks.registerBlockType',
	'gp-entry-blocks/conditional-logic/attribute',
	(settings) => {
		if (settings.name.indexOf('gp-entry-blocks/') !== 0) {
			settings.attributes = {
				...settings.attributes,
				gpebConditionalLogicEnabled: {
					type: 'boolean',
					default: false,
				},
				gpebConditionalLogic: {
					type: 'object',
				},
			};
		}

		return settings;
	}
);
